import { getConfig } from "config";
import { captureMessage } from "@sentry/react";

type Attempt = { scan: string; reservation?: string };
const key = () => `customer-deposit-unresolved:${getConfig().lockerId}`;
const recordKey = (kind: "scan" | "reservation", digest: string) => `${key()}:v2:${kind}:${digest}`;
const isDigest = (value: unknown): value is string =>
  typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
const reported = new Set<string>();

// The deployed boolean cannot identify a parcel. Preserve it for an operator,
// report it once, and keep unrelated deposits available.
function reportDeployedMarker() {
  const storageKey = key();
  if (localStorage.getItem(storageKey) === null || reported.has(storageKey)) return;
  reported.add(storageKey);
  captureMessage(
    "Unidentified customer deposit recovery requires operator reconciliation",
    "warning"
  );
}

function readRecord(kind: "scan" | "reservation", digest: string): Attempt | null {
  const raw = localStorage.getItem(recordKey(kind, digest));
  if (raw === null) return null;
  try {
    const entry = JSON.parse(raw);
    if (
      isDigest(entry?.scan) &&
      (entry.reservation === undefined || isDigest(entry.reservation)) &&
      (kind === "scan" ? entry.scan === digest : entry.reservation === digest)
    )
      return entry;
  } catch {
    // A damaged record still identifies its blocked identity through its key.
  }
  return {
    scan: kind === "scan" ? digest : "",
    reservation: kind === "reservation" ? digest : undefined,
  };
}

async function fingerprint(kind: "scan" | "reservation", value: string): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify([getConfig().lockerId, kind, value]));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export const fingerprintCustomerScan = (scan: string) => fingerprint("scan", scan);

export class UnresolvedCustomerDepositError extends Error {
  constructor() {
    super("This deposit requires operator reconciliation");
    this.name = "UnresolvedCustomerDepositError";
  }
}

export function isCustomerScanUnresolved(scan: string): boolean {
  reportDeployedMarker();
  return Boolean(readRecord("scan", scan));
}

export async function isReservationDepositUnresolved(
  reservation: Pick<Reservation, "id" | "tracking_number">
): Promise<boolean> {
  reportDeployedMarker();
  const reservationHash = reservation.id ? await fingerprint("reservation", reservation.id) : null;
  const scan = reservation.tracking_number
    ? await fingerprintCustomerScan(reservation.tracking_number)
    : null;
  const byReservation = reservationHash ? readRecord("reservation", reservationHash) : null;
  if (byReservation) return true;
  if (scan && readRecord("scan", scan)) return true;
  return false;
}

export async function assertReservationDepositAvailable(reservation: Reservation) {
  if (reservation.deposit && (await isReservationDepositUnresolved(reservation)))
    throw new UnresolvedCustomerDepositError();
}

export async function markCustomerScanUnresolved(scan: string, reservationId?: string) {
  if (
    isCustomerScanUnresolved(scan) ||
    (reservationId && (await isReservationDepositUnresolved({ id: reservationId })))
  )
    throw new UnresolvedCustomerDepositError();
  const reservation = reservationId ? await fingerprint("reservation", reservationId) : undefined;
  const serialized = JSON.stringify({ scan, reservation });
  // Separate records have no attempt-count limit and do not rewrite other parcels.
  // Persist and read back both identities before allowing a door command.
  const keys = [
    recordKey("scan", scan),
    ...(reservation ? [recordKey("reservation", reservation)] : []),
  ];
  const written: string[] = [];
  try {
    for (const storageKey of keys) {
      if (localStorage.getItem(storageKey) !== null) throw new UnresolvedCustomerDepositError();
      localStorage.setItem(storageKey, serialized);
      written.push(storageKey);
      if (localStorage.getItem(storageKey) !== serialized)
        throw new Error("Deposit recovery unavailable");
    }
  } catch (error) {
    // No door command has been admitted yet. Roll back this call's exact partial
    // writes so a storage error does not quarantine an unopened attempt.
    for (const storageKey of written) {
      try {
        if (localStorage.getItem(storageKey) === serialized) localStorage.removeItem(storageKey);
      } catch {
        // If rollback fails, keep the evidence and require reconciliation.
      }
    }
    throw error;
  }
}

export function clearCustomerScanUnresolved(scan: string) {
  try {
    const entry = readRecord("scan", scan);
    if (entry?.reservation && readRecord("reservation", entry.reservation)?.scan === scan)
      localStorage.removeItem(recordKey("reservation", entry.reservation));
    localStorage.removeItem(recordKey("scan", scan));
  } catch {
    // A retained identity requires reconciliation; never erase other attempts.
  }
}
